import React from 'react';

const PartnershipsSection = () => {
  // Technology partner logos - using actual company logos
  const getPartnerLogo = (name) => {
    const logoUrls = {
      'Snowflake': '/assets/images/SNOW-35164165-1768821853655.png',
      'AWS': 'https://cdn.worldvectorlogo.com/logos/aws-2.svg',
      'Microsoft Azure': 'https://cdn.worldvectorlogo.com/logos/azure-1.svg',
      'Google Cloud': 'https://cdn.worldvectorlogo.com/logos/google-cloud-1.svg',
      'Tableau': 'https://cdn.worldvectorlogo.com/logos/tableau-software.svg',
      'Power BI': 'https://cdn.worldvectorlogo.com/logos/power-bi.svg'
    };

    return (
      <img 
        src={logoUrls?.[name]} 
        alt={`${name} logo`}
        className="w-full h-full object-contain"
        onError={(e) => {
          // Fallback to generic icon if logo fails to load
          e.target.style.display = 'none';
        }}
      />
    );
  };

  const partnerships = [
    {
      name: 'Snowflake',
      alt: 'Snowflake cloud data platform logo',
      type: 'Cloud Data Platform',
      description: 'Specializing in cloud-native data warehousing solutions',
    },
    {
      name: 'AWS',
      alt: 'Amazon Web Services cloud computing logo',
      type: 'Cloud Infrastructure',
      description: 'Delivering scalable analytics solutions on AWS',
    },
    {
      name: 'Microsoft Azure',
      alt: 'Microsoft Azure cloud platform logo',
      type: 'Cloud Platform',
      description: 'Expert in Azure Synapse Analytics and Power BI implementations',
    },
    {
      name: 'Google Cloud',
      alt: 'Google Cloud Platform logo',
      type: 'Analytics Platform',
      description: 'Specialized in BigQuery and Google Analytics implementations',
    },
    {
      name: 'Tableau',
      alt: 'Tableau data visualization software logo',
      type: 'Business Intelligence',
      description: 'Enterprise data visualization solutions',
    },
    {
      name: 'Power BI',
      alt: 'Microsoft Power BI business analytics logo',
      type: 'Self-Service BI',
      description: 'Expert implementations of Microsoft business intelligence tools',
    }
  ];

  return (
    <section className="py-16 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-slate-800 mb-6">
            Strategic Technology <span className="text-[#002B45]">Deployment</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            We work with industry-leading technology to deliver best-in-class 
            data solutions tailored to your specific needs and existing infrastructure.
          </p>
        </div>

        {/* Partnerships Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {partnerships?.map((partner, index) => (
            <div 
              key={index} 
              className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-slate-200 p-8 group hover:border-[#1FB5A5] hover:scale-105 cursor-pointer"
            >
              <div className="flex flex-col items-center text-center">
                {/* Partner Logo - Now using actual company logos */}
                <div className="w-20 h-20 flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110">
                  {getPartnerLogo(partner?.name)}
                </div>

                {/* Partner Info */}
                <h3 className="text-xl font-bold text-slate-800 mb-2">
                  {partner?.name}
                </h3>
                <span className="inline-block px-3 py-1 bg-[#E6F0F5] text-[#002B45] rounded-full text-sm font-medium mb-4">
                  {partner?.type}
                </span>
                <p className="text-slate-600 leading-relaxed">
                  {partner?.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Partnership Benefits */}
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-slate-900 mb-4">
              Why Our Partnerships Matter
            </h3>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our partnerships accelerate value by combining independent strategic oversight with trusted, best-of-breed expertise where it matters most.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center transition-all duration-300 hover:scale-105 group">
              <div className="w-16 h-16 bg-[#E6F0F5] rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-300 group-hover:bg-[#D1E7ED] group-hover:scale-110">
                <svg className="w-8 h-8 text-[#002B45] transition-transform duration-300 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-slate-800 mb-2">Best-of-breed, not vendor-led</h4>
              <p className="text-slate-600">We partner selectively to ensure technology choices serve strategy, not the other way around.</p>
            </div>

            <div className="text-center transition-all duration-300 hover:scale-105 group">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-300 group-hover:bg-green-200 group-hover:scale-110">
                <svg className="w-8 h-8 text-green-600 transition-transform duration-300 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-slate-800 mb-2">Accelerated value delivery</h4>
              <p className="text-slate-600">Trusted partners reduce risk, speed up implementation, and turn strategy into impact faster.</p>
            </div>

            <div className="text-center transition-all duration-300 hover:scale-105 group">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-300 group-hover:bg-purple-200 group-hover:scale-110">
                <svg className="w-8 h-8 text-purple-600 transition-transform duration-300 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-slate-800 mb-2">Depth where it counts</h4>
              <p className="text-slate-600">Partnerships extend our expertise without diluting accountability or independence.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnershipsSection;